import { action, computed, observable } from 'mobx';
import CartService from './CartService';
import AuthService from './AuthService';
import { canUseWindow } from '../domain/ssrTools';

export default class CountryService {
  private static _instance: CountryService;
  private static EU = ['at', 'be', 'bg', 'hr', 'cy',
    'cz', 'dk', 'ee', 'fi', 'fr', 'de',
    'gr', 'hu', 'ie', 'it', 'lv', 'lt',
    'lu', 'mt', 'nl', 'pl', 'pt',
    'ro', 'sk', 'si', 'es', 'se'
  ];
  @observable
  private _country: GQL.ICountry;
  private _primaryCurrencyCode: string | undefined;

  @observable
  private _redirectCountryCode: string | null;

  public static GetInstance() {
    if (this._instance == undefined) {
      this._instance = new CountryService();
    }

    return this._instance;
  }

  @computed
  public get currency() {
    if (this._country) {
      return this._country.currency;
    }

    return null;
  }

  @computed
  public get country() {
    return this._country;
  }

  @computed
  public get name() {
    return this._country.name;
  }
  @computed
  public get countryId() {
    if (this._country && this._country.code) {
      return this._country.id;
    }

    return null;
  }

  @computed
  public get countryCode() {
    if (this._country && this._country.code) {
      return this._country.code;
    }

    return 'cz';
  }

  @computed
  public get shippingCountryCode() {
    // data přihlášeného uživatele
    const customerData = CartService.GetInstance().customerData;
    if (customerData) {
      // jiná doručovací země
      if ((customerData as any).AnotherDeliveryAddress === true) {
        if ((customerData as any)._DeliveryAddressCountry) {
          return (customerData as any)._DeliveryAddressCountry.code;
        }
      }
      // fakturační země
      if ((customerData as any)._AddressCountry) {
        return (customerData as any)._AddressCountry.code;
      }
    }

    if (this._country && this._country.code) {
      return this._country.code;
    }

    return 'cz';
  }

  @computed
  public get currencyCode() {
    if (this._country && this._country.currency) {
      return this._country.currency.value;
    }

    return 'EUR';
  }

  @computed
  public get language() {
    if (this._country && this._country.language && this._country.language.value) {
      return this._country.language.value;
    }

    return 'en';
  }

  @computed
  public get VATrate() {
    if (this._country) {
      // stát
      let countyState: string | null = null;
      // stát přihlášeného uživatele
      const customer = AuthService.GetInstance().customer;
      // osvobozen od DPH
      if (customer && (customer as any).saleTaxCertificate === true) {
        return 0;
      }

      // data z objednávky
      const { customerData } = CartService.GetInstance();
      // DPH je validní
      /* tslint:disable:no-string-literal */
      if (customerData && customerData['CompanyVatNumberValid'] === true
        && this.shippingCountryCode != 'cz'
      ) {
        return 0;
      }
      /* tslint:enable:no-string-literal */
      // konkrétní země
      let country = this._country;

      // vybraný stát země (pro doručení)
      /* tslint:disable:no-string-literal */
      if (customerData) {
        // země
        if (customerData['_AddressCountry'] && !Number.isNaN(Number(customerData['_AddressCountry'].VATrate))) {
          country = customerData['_AddressCountry'];
        }

        // HISTORIE - nebereme DPH z dorucovaci adresy!!!
        // OD *OSS* a změny pořadí košíku
        if (customerData['AnotherDeliveryAddress'] === true && customerData['_DeliveryAddressCountry'] && !Number.isNaN(Number(customerData['_DeliveryAddressCountry'].VATrate))) {
          country = customerData['_DeliveryAddressCountry'];
        }

        // stát země
        if (customerData['AddressCountyState']) {
          countyState = customerData['AddressCountyState'];
        }

        // Pro US necháváme VAT Rate podle delivery state
        if (customerData['AnotherDeliveryAddress'] === true && customerData['_DeliveryAddressCountry']) {
          country = customerData['_DeliveryAddressCountry'];
          countyState = customerData['DeliveryAddressCountyState'];
        }
      }

      /* tslint:enable:no-string-literal */
      // data vybraného státu | pouze USA je řízeno podle států
      if (country.states) {
        const countyStateObject = country.states.find((state: GQL.ICountryState) => {
          return (countyState !== null && state.name.toLowerCase() === countyState.toLowerCase());
        });
        if (countyStateObject && countyStateObject.vatRate) {
          return countyStateObject.vatRate;
        }
      }

      // DPH ze země
      return country.VATrate;
    }

    return 0;
  }

  public isEUMember(countryCode: string) {
    return CountryService.EU.includes(countryCode);
  }

  // Outside the EU and sold by the CZ company (no business = default CZ one), so the buyer pays import duties
  public isCustomsNoticeRequired(country: GQL.ICountry | null | undefined) {
    if (!country || !country.code) {
      return false;
    }

    const business = country.business;

    return !this.isEUMember(country.code) && (!business || business.country === 'Czech Republic');
  }

  public getEUMembers(countries: Array<GQL.ICountry>, isMember: boolean = true) {
    if (isMember) {
      return countries.filter(c => CountryService.EU.includes(c.code && c.code.toLowerCase() || '---'));
    }

    if (!isMember) {
      return countries.filter(c => !CountryService.EU.includes(c.code && c.code.toLowerCase() || '---'));
    }

    return [];
  }

  public areCountriesWithinAllowedRegions(countries: Array<GQL.ICountry>, invoiceCountry: string, deliveryCountry: string) {
    if (!countries) {
      return true;
    }

    const ic = countries.find(c => c.name === invoiceCountry);
    const dc = countries.find(c => c.name === deliveryCountry);

    if (!ic || !dc) {
      return true;
    }

    const isInvoiceEU = this.isEUMember(ic.code || '');
    const isDeliveryEU = this.isEUMember(dc.code || '');

    return isInvoiceEU === isDeliveryEU;
  }

  @action
  public setCountry(country: GQL.ICountry, initial: boolean = false, countries?: Array<GQL.ICountry>) {
    if (!country || !country.name) {
      return;
    }
    this._country = country;
    // must change even basket information if basket exists

    if (!initial) {
      const cart = CartService.GetInstance();
      const customerData = cart.customerData;
      if (
        // tslint:disable-next-line:no-string-literal
        customerData['AddressCountry']
      ) {
        cart.updateCustomerData({
          ...customerData,
          AddressCountry: country.name,
          _InvoiceAddressCountrySelectedByCustomer: country,
        });
      }
    }

    this.save();
  }

  @action
  public removeCountry() {
    if (canUseWindow()) {
      localStorage.removeItem('country');
    }
  }

  @action
  public setPrimaryCurrencyCode(code: string) {
    this._primaryCurrencyCode = code;
  }

  @computed
  public get currencyCodePageParam() {
    if (this._country && this._country.currency) {
      if (this._country.currency.value === this._primaryCurrencyCode) {
        return null;
      }
      return this._country.currency.value;
    }
    return null;
  }

  @action
  public setRedirectCountryCode(code: string | null) {
    this._redirectCountryCode = code;
  }

  @computed
  public get getRedirectCountryCode() {
    return this._redirectCountryCode;
  }

  private save() {
    if (canUseWindow()) {
      localStorage.setItem('country', JSON.stringify(this._country));
    }
  }

  private initialize() {
    if (canUseWindow()) {
      const tempCountry = localStorage.getItem('country');
      if (tempCountry) {
        this._country = JSON.parse(tempCountry);
      }
    }
  }

  private constructor() {
    this.initialize();
  }
}



// WEBPACK FOOTER //
// ./src/services/CountryService.ts